import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    Divider,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LoginIcon from '@mui/icons-material/Login';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface LoginPromptModalProps {
    open: boolean;
    onClose: () => void;
    onGuestContinue?: (email: string) => void;
    title?: string;
    message?: string;
    redirectPath?: string;
    featureName?: string;
    allowGuestEmail?: boolean;
}

type ModalView = 'options' | 'guest-email';

const LoginPromptModal: React.FC<LoginPromptModalProps> = ({
    open,
    onClose,
    onGuestContinue,
    title = 'Save Your Trip',
    message,
    redirectPath,
    featureName = 'this feature',
    allowGuestEmail = true,
}) => {
    const navigate = useNavigate();
    const [view, setView] = useState<ModalView>('options');
    const [guestEmail, setGuestEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [loading, setLoading] = useState(false);

    const defaultMessage = `Choose how you'd like to continue.`;

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleLoginSignUp = () => {
        const redirect = redirectPath || window.location.pathname;
        navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
        handleClose();
    };

    const handleGuestEmailClick = () => {
        setView('guest-email');
        setEmailError('');
    };

    const handleBackToOptions = () => {
        setView('options');
        setEmailError('');
    };

    const handleGuestContinue = async () => {
        if (!guestEmail.trim()) {
            setEmailError('Please enter your email address');
            return;
        }

        if (!validateEmail(guestEmail)) {
            setEmailError('Please enter a valid email address');
            return;
        }

        setLoading(true);
        setEmailError('');

        try {
            localStorage.setItem('guestEmail', guestEmail);
            
            if (onGuestContinue) {
                await onGuestContinue(guestEmail);
            }
            
            handleClose();
        } catch (error) {
            setEmailError('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setView('options');
        setGuestEmail('');
        setEmailError('');
        onClose();
    };

    // Guest Email Entry View
    if (view === 'guest-email') {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        p: 1,
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            onClick={handleBackToOptions}
                            sx={{ minWidth: 'auto', p: 0.5, color: 'text.secondary' }}
                        >
                            <ArrowBackIcon />
                        </Button>
                        <Typography variant="h6" fontWeight={600}>
                            Continue as Guest
                        </Typography>
                    </Box>
                </DialogTitle>
                
                <DialogContent sx={{ py: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Enter your email to save your trip. We'll send you a confirmation.
                    </Typography>

                    {emailError && (
                        <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                            {emailError}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={guestEmail}
                        onChange={(e) => {
                            setGuestEmail(e.target.value);
                            setEmailError('');
                        }}
                        placeholder="you@example.com"
                        autoFocus
                        InputProps={{
                            sx: { borderRadius: '12px' }
                        }}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleGuestContinue();
                            }
                        }}
                    />
                </DialogContent>
                
                <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3 }}>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleGuestContinue}
                        disabled={loading}
                        sx={{
                            bgcolor: '#2D6E6A',
                            '&:hover': { bgcolor: '#009688' },
                            borderRadius: '24px',
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Continue'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // Main Options View
    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    p: 1,
                }
            }}
        >
            <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                <Box
                    sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: 'rgba(0, 180, 160, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                    }}
                >
                    <LockOutlinedIcon sx={{ fontSize: 30, color: '#2D6E6A' }} />
                </Box>
                <Typography variant="h5" fontWeight={600}>
                    {title}
                </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body1" color="text.secondary">
                    {message || defaultMessage}
                </Typography>
            </DialogContent>
            
            <DialogActions sx={{ flexDirection: 'column', gap: 1.5, px: 3, pb: 3 }}>
                {/* Continue as Guest - Primary option */}
                {allowGuestEmail && (
                    <>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleGuestEmailClick}
                            startIcon={<EmailOutlinedIcon />}
                            sx={{
                                bgcolor: '#2D6E6A',
                                '&:hover': { bgcolor: '#009688' },
                                borderRadius: '24px',
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            Continue as Guest
                        </Button>

                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 2, my: 0.5 }}>
                            <Divider sx={{ flex: 1 }} />
                            <Typography variant="caption" color="text.secondary">
                                or
                            </Typography>
                            <Divider sx={{ flex: 1 }} />
                        </Box>
                    </>
                )}

                {/* Log In / Sign Up */}
                <Button
                    fullWidth
                    variant={allowGuestEmail ? "outlined" : "contained"}
                    onClick={handleLoginSignUp}
                    startIcon={<LoginIcon />}
                    sx={{
                        borderColor: '#2D6E6A',
                        color: allowGuestEmail ? '#2D6E6A' : '#fff',
                        bgcolor: allowGuestEmail ? 'transparent' : '#2D6E6A',
                        '&:hover': {
                            borderColor: '#009688',
                            bgcolor: allowGuestEmail ? 'rgba(0, 180, 160, 0.05)' : '#009688',
                        },
                        borderRadius: '24px',
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    Log In / Sign Up
                </Button>
                
                <Button
                    fullWidth
                    onClick={handleClose}
                    sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                        mt: 0.5,
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default LoginPromptModal;
